import { Link, NavLink } from "react-router-dom";
import type { AuthUser } from "@/providers/AuthProvider";

interface TopBarProps {
  currentUser: AuthUser | null;
  onLogout?: () => void;
}

export function TopBar({ currentUser, onLogout }: TopBarProps): JSX.Element {
  return (
    <header
      data-app-chrome
      className="relative z-40 flex shrink-0 items-center justify-between gap-4 border-b border-slate-200/80 bg-white px-4 py-3 shadow-sm md:px-6"
    >
      <div className="flex min-w-0 items-center gap-3 md:gap-6">
        <Link
          to="/children"
          aria-label="Child+ 儿童首页"
          className="flex min-h-11 shrink-0 items-center gap-2 rounded-xl focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-2"
        >
          <img
            src="/childplus-logo.webp"
            alt=""
            aria-hidden="true"
            className="h-9 w-auto max-w-[7.25rem] rounded-md object-contain ring-1 ring-slate-900/[0.04] sm:h-10 sm:max-w-[8rem]"
          />
          <span className="hidden leading-tight lg:block">
            <strong className="block text-base font-semibold text-brand-dark">
              Child+智慧平台
            </strong>
            <span className="block whitespace-nowrap text-xs text-slate-500">
              活动评估 联结系统 儿童发展
            </span>
          </span>
        </Link>
        <nav aria-label="全局导航" className="flex items-center gap-2">
          <TopNavLink to="/children">全部儿童</TopNavLink>
          <TopNavLink to="/scale">查看量表</TopNavLink>
          {currentUser?.isSuperAdmin ? <TopNavLink to="/accounts">账号管理</TopNavLink> : null}
        </nav>
      </div>
      <div className="flex min-w-0 items-center gap-2 md:gap-3">
        <div className="hidden text-right xl:block">
          <p className="text-sm font-semibold text-slate-700">
            {currentUser?.displayName ?? "未登录"}
          </p>
        </div>
        <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-brand to-brand-dark text-sm font-semibold text-white shadow-lg shadow-brand/20">
          {currentUser?.displayName.slice(0, 1) ?? "?"}
        </span>
        {currentUser ? (
          <button
            type="button"
            onClick={onLogout}
            className="inline-flex min-h-11 items-center rounded-lg border border-slate-200 px-3 text-xs font-semibold text-slate-600 transition hover:border-brand hover:text-brand focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-2"
          >
            退出
          </button>
        ) : null}
      </div>
    </header>
  );
}

function TopNavLink({ to, children }: { to: string; children: string }): JSX.Element {
  return (
    <NavLink
      to={to}
      end
      className={({ isActive }) =>
        `inline-flex min-h-11 items-center rounded-xl border px-4 text-sm font-semibold shadow-sm transition-[color,background-color,border-color,box-shadow] duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-2 ${
          isActive
            ? "border-brand bg-brand text-white shadow-brand/20"
            : "border-slate-200 bg-white text-slate-700 hover:border-brand/40 hover:bg-brand/5 hover:text-brand-dark"
        }`
      }
    >
      {children}
    </NavLink>
  );
}
